import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import SubscriptionModal from '../components/SubscriptionModal';
import {
  getAllSubscriptions,
  deleteSubscription,
  getSubscriptionStats,
  toMonthlyAmount,
} from '../services/subscriptionService';
import type { Subscription, SubscriptionStatus } from '../types/subscription';
import { getPresetOrDefault } from '../config/servicePresets';
import {
  Plus, Search, Pencil, Trash2, CreditCard,
  TrendingUp, LayoutGrid, List, AlertTriangle,
  CheckCircle2, Clock, PauseCircle, XCircle,
  SlidersHorizontal, CalendarDays,
} from 'lucide-react';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<SubscriptionStatus, { label: string; color: string; icon: any }> = {
  active:    { label: 'Active',    color: 'text-emerald-400 bg-emerald-400/10', icon: CheckCircle2 },
  trial:     { label: 'Trial',     color: 'text-indigo-400 bg-indigo-400/10',   icon: Clock },
  paused:    { label: 'Paused',    color: 'text-amber-400 bg-amber-400/10',     icon: PauseCircle },
  cancelled: { label: 'Cancelled', color: 'text-rose-400 bg-rose-400/10',       icon: XCircle },
};

// ─── Days until ───────────────────────────────────────────────────────────────
const daysUntil = (dateStr?: string): number | null => {
  if (!dateStr) return null;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ─── Subscription Card ────────────────────────────────────────────────────────
const SubCard = ({
  sub, onEdit, onDelete,
}: {
  sub: Subscription;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const preset = getPresetOrDefault(sub.logoKey);
  const statusCfg = STATUS_CONFIG[sub.status];
  const StatusIcon = statusCfg.icon;
  const monthly = toMonthlyAmount(sub.amount, sub.billingCycle);
  const days = daysUntil(sub.nextBillingDate);
  const isDueSoon = days !== null && days >= 0 && days <= 7;
  const isOverdue = days !== null && days < 0;

  const CYCLE_LABEL: Record<string, string> = {
    weekly: '/wk', monthly: '/mo', quarterly: '/qtr', yearly: '/yr',
  };

  return (
    <div className="glass-card rounded-2xl p-5 hover:border-white/15 transition-all duration-200 fade-in-up flex flex-col gap-4">
      {/* Top row */}
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-xl"
          style={{ background: preset.color }}
        >
          {preset.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{sub.customName || sub.serviceName}</p>
          <p className="text-xs text-gray-500 capitalize mt-0.5">{sub.category}</p>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-base font-bold text-white">
            {sub.currency} {sub.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500">{CYCLE_LABEL[sub.billingCycle]}</p>
        </div>
      </div>

      {/* Middle: status + due date */}
      <div className="flex items-center justify-between">
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
          <StatusIcon className="h-3 w-3" />
          {statusCfg.label}
        </div>
        {sub.nextBillingDate && sub.status !== 'cancelled' && (
          <div className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-400' : isDueSoon ? 'text-amber-400' : 'text-gray-500'}`}>
            <CalendarDays className="h-3 w-3" />
            {isOverdue
              ? `${Math.abs(days!)}d overdue`
              : days === 0
              ? 'Due today'
              : `${days}d left`}
          </div>
        )}
      </div>

      {/* Monthly equivalent (if not already monthly) */}
      {sub.billingCycle !== 'monthly' && sub.status !== 'cancelled' && (
        <div className="rounded-lg bg-white/3 border border-white/5 px-3 py-2 text-xs text-gray-400 flex justify-between">
          <span>Monthly equivalent</span>
          <span className="text-white font-medium">
            {sub.currency} {monthly.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
      )}

      {sub.notes && (
        <p className="text-xs text-gray-600 truncate">{sub.notes}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-gray-400 bg-white/5 hover:bg-white/10 hover:text-white transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
        <button
          onClick={() => { if (!confirmDelete) { setConfirmDelete(true); return; } onDelete(); }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            confirmDelete
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
              : 'bg-white/5 text-gray-400 hover:text-red-400 hover:bg-red-400/10'
          }`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {confirmDelete ? 'Confirm?' : 'Delete'}
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Subscriptions = () => {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [stats, setStats] = useState(getSubscriptionStats());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<Subscription | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const refresh = useCallback(() => {
    setSubs(getAllSubscriptions());
    setStats(getSubscriptionStats());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = subs
    .filter((s) => statusFilter === 'all' || s.status === statusFilter)
    .filter((s) =>
      !search ||
      (s.customName || s.serviceName).toLowerCase().includes(search.toLowerCase()) ||
      (s.notes || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      // Active first, then trial, paused, cancelled
      const order: Record<SubscriptionStatus, number> = { active: 0, trial: 1, paused: 2, cancelled: 3 };
      return order[a.status] - order[b.status];
    });

  // Due-soon count (next 7 days, active only)
  const dueSoonCount = subs.filter((s) => {
    if (s.status !== 'active') return false;
    const d = daysUntil(s.nextBillingDate);
    return d !== null && d >= 0 && d <= 7;
  }).length;

  const fmt = (n: number) =>
    `USD ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const STATUS_FILTERS: { label: string; value: SubscriptionStatus | 'all' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Trial', value: 'trial' },
    { label: 'Paused', value: 'paused' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex items-start justify-between mb-8 fade-in-up">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-violet-500/15 flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-violet-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
              </div>
              <p className="text-sm text-gray-500">
                Track all your recurring services and spending
              </p>
            </div>
            <button
              id="addSubscriptionBtn"
              onClick={() => { setEditingSub(null); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-500 transition-colors shadow-lg shadow-violet-500/25 flex-shrink-0"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              {
                label: 'Monthly Cost',
                value: fmt(stats.monthlyTotal),
                sub: 'Active subscriptions',
                icon: TrendingUp,
                accent: 'text-violet-400 bg-violet-400/10',
              },
              {
                label: 'Annual Cost',
                value: fmt(stats.yearlyTotal),
                sub: 'Projected yearly spend',
                icon: TrendingUp,
                accent: 'text-indigo-400 bg-indigo-400/10',
              },
              {
                label: 'Active',
                value: String(stats.activeCount),
                sub: `${stats.trialCount} in trial`,
                icon: CheckCircle2,
                accent: 'text-emerald-400 bg-emerald-400/10',
              },
              {
                label: 'Renewing Soon',
                value: String(dueSoonCount),
                sub: dueSoonCount > 0 ? 'Due within 7 days' : 'Nothing due soon',
                icon: AlertTriangle,
                accent: dueSoonCount > 0 ? 'text-amber-400 bg-amber-400/10' : 'text-gray-400 bg-gray-400/10',
              },
            ].map((card) => (
              <div key={card.label} className="glass-card rounded-2xl p-5 fade-in-up">
                <div className="flex items-start justify-between mb-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.label}</p>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${card.accent}`}>
                    <card.icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-xl font-bold text-white">{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Filters + search + view toggle */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6 fade-in-up delay-100">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
                placeholder="Search subscriptions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      statusFilter === f.value ? 'bg-violet-600 text-white' : 'text-gray-400 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1 ml-2 border-l pl-2" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'text-violet-400 bg-violet-400/10' : 'text-gray-500 hover:text-white'}`}>
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'text-violet-400 bg-violet-400/10' : 'text-gray-500 hover:text-white'}`}>
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* List */}
          {filtered.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center fade-in-up delay-200">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-7 w-7 text-violet-400" />
              </div>
              <p className="text-white font-medium mb-1">
                {search ? 'No results found' : 'No subscriptions yet'}
              </p>
              <p className="text-sm text-gray-500">
                {search ? 'Try a different search.' : 'Click "Add" to start tracking your services.'}
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((s) => (
                <SubCard
                  key={s.id}
                  sub={s}
                  onEdit={() => { setEditingSub(s); setModalOpen(true); }}
                  onDelete={() => { deleteSubscription(s.id); refresh(); }}
                />
              ))}
            </div>
          ) : (
            /* List view */
            <div className="space-y-2">
              {filtered.map((s) => {
                const preset = getPresetOrDefault(s.logoKey);
                const statusCfg = STATUS_CONFIG[s.status];
                const StatusIcon = statusCfg.icon;
                const monthly = toMonthlyAmount(s.amount, s.billingCycle);
                const days = daysUntil(s.nextBillingDate);
                const CYCLE_LABEL: Record<string, string> = { weekly: '/wk', monthly: '/mo', quarterly: '/qtr', yearly: '/yr' };
                return (
                  <div key={s.id} className="glass-card rounded-xl px-4 py-3 flex items-center gap-4 hover:border-white/15 transition-all">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: preset.color }}>
                      {preset.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{s.customName || s.serviceName}</p>
                      <p className="text-xs text-gray-500 capitalize">{s.category}</p>
                    </div>
                    <div className={`hidden sm:flex items-center gap-1 text-xs px-2 py-1 rounded-full ${statusCfg.color}`}>
                      <StatusIcon className="h-3 w-3" /> {statusCfg.label}
                    </div>
                    {days !== null && s.status !== 'cancelled' && (
                      <p className={`hidden md:block text-xs w-20 text-right ${days < 0 ? 'text-red-400' : days <= 7 ? 'text-amber-400' : 'text-gray-500'}`}>
                        {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                      </p>
                    )}
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-white">{s.currency} {s.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}<span className="text-xs text-gray-500 font-normal ml-1">{CYCLE_LABEL[s.billingCycle]}</span></p>
                      {s.billingCycle !== 'monthly' && <p className="text-xs text-gray-500">{s.currency} {monthly.toFixed(2)}/mo</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditingSub(s); setModalOpen(true); }} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => { deleteSubscription(s.id); refresh(); }} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <SubscriptionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={refresh}
        editingSub={editingSub}
      />
    </div>
  );
};

export default Subscriptions;
